// Customer account math (roadmap Phase 1.5), with no Firebase or DOM so tests/unit can run it:
// balances and aging from the app's own invoices and payments. accounts.js loads the data and
// draws the screens.

export const BUCKETS = [
  ['current', 'Current'], ['d30', '1–30'], ['d60', '31–60'], ['d90', '61–90'], ['d90p', '90+'],
];

export function todayYmd(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
export const daysBetween = (from, to) => Math.round((Date.parse(to) - Date.parse(from)) / 86400000);
export const fmtDate = ymd => {
  if (!ymd) return '';
  const [y, m, d] = String(ymd).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export function bucketFor(dueDate, today) {
  if (!dueDate) return 'current';
  const late = daysBetween(dueDate, today);
  if (late <= 0) return 'current';
  if (late <= 30) return 'd30';
  if (late <= 60) return 'd60';
  if (late <= 90) return 'd90';
  return 'd90p';
}

const isOpen = inv => inv && (inv.status === 'sent' || inv.status === 'partial') && inv.balanceCents > 0;

/** Every customer with an invoice or payment → { name, email, buckets, openCents, creditCents, balanceCents, overdueCents, openInvoices[] }. */
export function summarize(invoices, payments, today = todayYmd()) {
  const out = new Map();
  const acct = (id, name, email) => {
    if (!out.has(id)) out.set(id, { customerId: id, name: name || 'Customer', email: email || '', buckets: Object.fromEntries(BUCKETS.map(([k]) => [k, 0])), openCents: 0, creditCents: 0, balanceCents: 0, overdueCents: 0, openInvoices: [] });
    const a = out.get(id);
    if (name && a.name === 'Customer') a.name = name;
    if (email && !a.email) a.email = email;
    return a;
  };
  Object.values(invoices || {}).forEach(inv => {
    if (!inv?.customerId || !inv.number) return;
    const a = acct(inv.customerId, inv.customerName, inv.customerEmail);
    if (!isOpen(inv)) return;
    const b = bucketFor(inv.dueDate, today);
    a.buckets[b] += inv.balanceCents;
    a.openCents += inv.balanceCents;
    if (b !== 'current') a.overdueCents += inv.balanceCents;
    a.openInvoices.push(inv);
  });
  Object.values(payments || {}).forEach(p => {
    if (!p?.customerId || p.status !== 'ok') return;
    const a = acct(p.customerId, p.customerName, p.customerEmail);
    a.creditCents += Number(p.unappliedCents) || 0;
  });
  out.forEach(a => {
    a.balanceCents = a.openCents - a.creditCents;
    a.openInvoices.sort((x, y) => String(x.dueDate).localeCompare(String(y.dueDate)));
  });
  return out;
}

/** A customer's history: issued invoices (+), payments (−), voids noted; with a running balance. */
export function ledger(customerId, invoices, payments) {
  const rows = [];
  Object.values(invoices || {}).forEach(inv => {
    if (inv?.customerId !== customerId || !inv.number) return;
    rows.push({ date: inv.issuedDate || '', kind: 'invoice', ref: inv.number, id: inv.id, cents: inv.status === 'void' ? 0 : inv.totalCents, void: inv.status === 'void', text: inv.boatName || '' });
  });
  Object.values(payments || {}).forEach(p => {
    if (p?.customerId !== customerId) return;
    rows.push({ date: p.receivedDate || '', kind: 'payment', ref: p.number, id: p.id, cents: p.status === 'void' ? 0 : -p.amountCents, void: p.status === 'void', text: p.method });
  });
  rows.sort((a, b) => a.date.localeCompare(b.date) || (a.kind === 'invoice' ? -1 : 1));
  let run = 0;
  rows.forEach(r => { run += r.cents; r.balance = run; });
  return rows;
}

/**
 * The statement's account summary: everything issued to the customer (voids and drafts left out) and
 * every payment received (voids left out). Invoiced − paid is the amount due, since unapplied money is
 * credit; adjustCents holds any difference from balanceCents so the summary always adds up.
 */
export function accountTotals(customerId, invoices, payments, balanceCents) {
  let invoicedCents = 0, paidCents = 0, since = '';
  Object.values(invoices || {}).forEach(inv => {
    if (inv?.customerId !== customerId || !inv.number || inv.status === 'void' || inv.status === 'draft') return;
    invoicedCents += Number(inv.totalCents) || 0;
    if (inv.issuedDate && (!since || inv.issuedDate < since)) since = inv.issuedDate;
  });
  Object.values(payments || {}).forEach(p => {
    if (p?.customerId !== customerId || p.status !== 'ok') return;
    paidCents += Number(p.amountCents) || 0;
  });
  return { since, invoicedCents, paidCents, adjustCents: balanceCents - (invoicedCents - paidCents) };
}
